import _ from 'lodash';
import { createSelector } from 'reselect';

import {
  get,
  post,
  put,
  del, } from 'utils/fetch';
import { 
  createAction,
  entity_reducer,
  list_reducer,
  list_remove } from 'utils/store';
import { getTwitchAuthURL, openAuthWindow } from 'utils/twitch';
import { withHost } from 'utils/url';
import { Storage, RequestState } from 'constants/constants';

import { selectApiHost, selectConfig } from 'store/AppState';
import RouteState from 'store/RouteState';
import Channel from 'store/models/Channel';



export const FETCH_USER = 'FETCH_USER';
export const CREATE_USER = 'CREATE_USER';
export const UPDATE_USER = 'UPDATE_USER';

export const START_SESSION = 'START_SESSION';
export const AUTH_USER = 'AUTH_USER';
export const LOGIN_USER = 'LOGIN_USER';
export const REQUEST_RESET_PASSWORD = 'REQUEST_RESET_PASSWORD';
export const RESET_PASSWORD = 'RESET_PASSWORD';

export const SET_LOGIN = 'SET_LOGIN';
export const LOGOUT_USER = 'LOGOUT_USER';

// ======================
// HELPERS
// ====================== 

export const isAdmin = user => _.get(user, 'type') === 'admin';
export const canAddChannels = user => user?.permissions?.addChannels ?? false;


// ======================
// SELECTORS
// ====================== 
export const selectUserById = (state, id) => state.models.users[id];
export const selectUsers = createSelector(
  (state) => state.models.users,
  (s, ids) => ids,
  (users, ids) => _.pick(users, ids));
export const selectCurrentUserId = state => state.app.authUser?.userId;
export const selectCurrentUser = state => selectUserById(state, selectCurrentUserId(state));


// ======================
// MODEL
// ====================== 

const User = {
  actions: {
    getUserById: (id) => {
      return (dispatch, getState) => get({
        url: "/api/user/:id", 
        params: { id },
        dispatch, 
        action: FETCH_USER,
      })
    },

    getUserByToken: (token) => {
      return (dispatch, getState) => get({
        url: "/api/user/token/:token", 
        params: { token },
        dispatch, 
        action: FETCH_USER,
      });
    },

    createUser: (values) => {
      return (dispatch, getState) => post({
        url: "/api/user", 
        body: values,
        dispatch, 
        action: CREATE_USER,
      });
    },

    updateUser: (id, values) => {
      return (dispatch, getState) => put({
        url: "/api/user/:id",
        params: { id },
        body: { values },
        dispatch, 
        action: UPDATE_USER,
      });
    },





    // ======================
    // LOGIN / SIGNUP
    // ====================== 
    authUser: (values, type = 'twitch') => async (dispatch, getState) => {
      let response = await post({
        url: "/api/user/auth/:type", 
        params: { type },
        body: values,
        dispatch, 
        action: AUTH_USER,
      });
      await dispatch(User.actions.handleAuthResponse(response));
      dispatch(RouteState.actions.navigate('/dashboard'));
    },

    loginUser: (username, password) => async (dispatch, getState) => {
      let response = await post({
        url: "/api/user/login",
        body: { username, password },
        dispatch, 
        action: LOGIN_USER,
      });
      await dispatch(User.actions.handleAuthResponse(response));
      dispatch(RouteState.actions.navigate('/dashboard'));
    },

    signup: values => async dispatch => {
      let response = await dispatch(User.actions.createUser(values));
      await dispatch(User.actions.handleAuthResponse(response));
      dispatch(RouteState.actions.navigate('/dashboard'));
    },

    
    // ======================
    // AUTH
    // ====================== 
    checkLogin: () => async (dispatch, getState) => {
      let state = getState();
      let userId = selectCurrentUserId(state);

      if(!userId)
        return dispatch(User.actions.logoutUser());

      try {
        let response = await dispatch(User.actions.startSession(userId))
        return dispatch(User.actions.handleAuthResponse(response));
      } catch(err) {
        if(err && err.code === 404)
          return dispatch(User.actions.logoutUser());
      }
    },
    
    startSession: () => async (dispatch, getState) => get({
      url: "/api/user/start",
      dispatch,
      action: START_SESSION,
    }),

    handleAuthResponse: response => async dispatch => {
      let userId = response?.body?.users?.[0]?._id;
      let accountId = response?.body?.accounts?.[0]?._id;
      if(!userId)
        return dispatch(User.actions.logoutUser());
      
      await dispatch(User.actions.setLogin(userId, accountId));
      await dispatch(Channel.actions.fetchChannels());
      return Promise.resolve(response);
    },

    setLogin: (userId, accountId, username, userType) => {
      return { 
        type: SET_LOGIN, 
        userId, 
        accountId, 
        username, 
        userType };
    },

    logoutUser: () => async (dispatch, getState) => {
      // kill JWT
      await get({
        url: "/api/user/logout",
        dispatch,
        action: LOGOUT_USER,
      })
      
      // redirect location
      window.location = '/';
        
    },

    twitchAuth: () => {
      return (dispatch, getState) => {
        let state = getState();
        let config = selectConfig(state);
        let redirect = withHost(config.auth.redirect);
        let url = getTwitchAuthURL(config.twitch.client, config.twitch.scopes, redirect);
        return openAuthWindow(url).then(code => {
          return dispatch(User.actions.authUser({...code, redirect }));
        });
      }
    },


    // ======================
    // RESET PASSWORD
    // ====================== 
    requestResetPassword: (email) => async (dispatch) => post({
      url: "/api/user/auth/request_reset_password",
      body: { email },
      dispatch,
      action: REQUEST_RESET_PASSWORD,
    }),

    resetPassword: (password, userId, token) => async (dispatch) => post({
      url: "/api/user/auth/reset_password",
      body: { password, userId, token },
      dispatch,
      action: RESET_PASSWORD,
    }),

  },
  spec: {
    users: {},
  },
  reducer: (state, type, body, action, json) => {

    if (action.url && action.result !== RequestState.SUCCESS) 
      return state;

    if(body && body.users) {
      let users = _.map(body.users, user => _.set(user, 'username', _.replace(user.username, '_bot', '')))
      return { ...state, 
        users: entity_reducer(state.users, users, '_id'), }
    }
    return state;
  },
}

export default User;

